// =============================================
// DrawingPad — a lightweight, dependency-free canvas sketch pad (modal)
// =============================================
// Produces BOTH a rendered PNG (for inline display) and a vector scene (strokes
// JSON, so the drawing reopens for editing). Tools: pen, highlighter, eraser,
// colours, stroke sizes, undo, clear. No external libraries.
// =============================================
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FiX } from 'react-icons/fi';

const W = 800;
const H = 500;

type Tool = 'pen' | 'highlighter' | 'eraser';
interface Stroke { tool: Tool; color: string; width: number; points: number[][]; }
export interface Scene { v: number; width: number; height: number; strokes: Stroke[]; }

const COLORS = ['#1f2937', '#dc2626', '#2563eb', '#16a34a', '#E8862A', '#facc15'];
const SIZES: { label: string; size: number }[] = [
  { label: 'S', size: 3 }, { label: 'M', size: 6 }, { label: 'L', size: 10 },
];

interface Props {
  initialScene?: Scene | null;
  title?: string;
  onSave: (scene: Scene, png: Blob) => void | Promise<void>;
  onCancel: () => void;
}

function effective(tool: Tool, color: string, size: number): { color: string; width: number } {
  if (tool === 'eraser') return { color: '#ffffff', width: size * 4 };
  if (tool === 'highlighter') return { color, width: size * 4 };
  return { color, width: size };
}

const DrawingPad: React.FC<Props> = ({ initialScene, title, onSave, onCancel }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const strokesRef = useRef<Stroke[]>(initialScene?.strokes ? initialScene.strokes.map(s => ({ ...s, points: s.points.slice() })) : []);
  const drawingRef = useRef<Stroke | null>(null);
  const [tool, setTool] = useState<Tool>('pen');
  const [color, setColor] = useState(COLORS[0]);
  const [size, setSize] = useState(SIZES[0].size);
  const [saving, setSaving] = useState(false);
  const [, forceRerender] = useState(0); // toolbar "undo disabled" refresh

  const ctx = () => canvasRef.current?.getContext('2d') || null;

  const paintStroke = useCallback((c: CanvasRenderingContext2D, s: Stroke) => {
    if (s.points.length === 0) return;
    c.save();
    c.globalAlpha = s.tool === 'highlighter' ? 0.3 : 1;
    c.strokeStyle = s.color;
    c.lineWidth = s.width;
    c.lineCap = 'round';
    c.lineJoin = 'round';
    c.beginPath();
    c.moveTo(s.points[0][0], s.points[0][1]);
    if (s.points.length === 1) { c.lineTo(s.points[0][0] + 0.01, s.points[0][1]); }
    else s.points.forEach((p, i) => { if (i > 0) c.lineTo(p[0], p[1]); });
    c.stroke();
    c.restore();
  }, []);

  const redrawAll = useCallback(() => {
    const c = ctx();
    if (!c) return;
    c.fillStyle = '#ffffff';
    c.fillRect(0, 0, W, H);
    strokesRef.current.forEach(s => paintStroke(c, s));
  }, [paintStroke]);

  useEffect(() => { redrawAll(); }, [redrawAll]);

  const toCanvasXY = (e: React.PointerEvent): [number, number] => {
    const el = canvasRef.current!;
    const r = el.getBoundingClientRect();
    return [(e.clientX - r.left) * (W / r.width), (e.clientY - r.top) * (H / r.height)];
  };

  const onPointerDown = (e: React.PointerEvent) => {
    e.preventDefault();
    (e.target as Element).setPointerCapture?.(e.pointerId);
    const { color: col, width } = effective(tool, color, size);
    const s: Stroke = { tool, color: col, width, points: [toCanvasXY(e)] };
    drawingRef.current = s;
    strokesRef.current.push(s);
    const c = ctx();
    if (c) paintStroke(c, s);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const s = drawingRef.current;
    if (!s) return;
    e.preventDefault();
    const [x, y] = toCanvasXY(e);
    const prev = s.points[s.points.length - 1];
    s.points.push([x, y]);
    const c = ctx();
    if (!c) return;
    // Draw just the new segment for smoothness (highlighter re-renders on undo/load).
    c.save();
    c.globalAlpha = s.tool === 'highlighter' ? 0.3 : 1;
    c.strokeStyle = s.color;
    c.lineWidth = s.width;
    c.lineCap = 'round';
    c.lineJoin = 'round';
    c.beginPath();
    c.moveTo(prev[0], prev[1]);
    c.lineTo(x, y);
    c.stroke();
    c.restore();
  };

  const endStroke = () => { if (drawingRef.current) { drawingRef.current = null; forceRerender(n => n + 1); } };

  const undo = () => { strokesRef.current.pop(); redrawAll(); forceRerender(n => n + 1); };
  const clear = () => { strokesRef.current = []; redrawAll(); forceRerender(n => n + 1); };

  const save = async () => {
    const el = canvasRef.current;
    if (!el) return;
    setSaving(true);
    const blob: Blob | null = await new Promise(res => el.toBlob(b => res(b), 'image/png'));
    if (!blob) { setSaving(false); return; }
    const scene: Scene = { v: 1, width: W, height: H, strokes: strokesRef.current };
    try { await onSave(scene, blob); } finally { setSaving(false); }
  };

  return (
    <div className="draw-overlay" onMouseDown={e => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="draw-modal" role="dialog" aria-label="Drawing pad">
        <div className="draw-head">
          <strong>{title || 'Drawing'}</strong>
          <button className="draw-x" onClick={onCancel} aria-label="Close"><FiX size={16} /></button>
        </div>

        <div className="draw-tools">
          <div className="draw-group">
            {(['pen', 'highlighter', 'eraser'] as Tool[]).map(t => (
              <button key={t} className={`draw-tool ${tool === t ? 'on' : ''}`} onClick={() => setTool(t)}>
                {t === 'pen' ? '✏️ Pen' : t === 'highlighter' ? '🖍️ Mark' : '🧽 Erase'}
              </button>
            ))}
          </div>
          <div className="draw-group">
            {COLORS.map(c => (
              <button
                key={c}
                className={`draw-swatch ${color === c && tool !== 'eraser' ? 'on' : ''}`}
                style={{ background: c }}
                onClick={() => { setColor(c); if (tool === 'eraser') setTool('pen'); }}
                aria-label={`Colour ${c}`}
              />
            ))}
          </div>
          <div className="draw-group">
            {SIZES.map(s => (
              <button key={s.label} className={`draw-size ${size === s.size ? 'on' : ''}`} onClick={() => setSize(s.size)}>
                <span style={{ width: s.size + 2, height: s.size + 2 }} />
              </button>
            ))}
          </div>
          <div className="draw-group">
            <button className="draw-act" onClick={undo} disabled={strokesRef.current.length === 0}>Undo</button>
            <button className="draw-act" onClick={clear} disabled={strokesRef.current.length === 0}>Clear</button>
          </div>
        </div>

        <div className="draw-canvas-wrap">
          <canvas
            ref={canvasRef}
            width={W}
            height={H}
            className="draw-canvas"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endStroke}
            onPointerLeave={endStroke}
          />
        </div>

        <div className="draw-actions">
          <button className="draw-cancel" onClick={onCancel}>Cancel</button>
          <button className="draw-save" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Insert drawing'}</button>
        </div>
      </div>
    </div>
  );
};

export default DrawingPad;
